import { PageContainer } from '@amelio/ui-library';
import { useState } from 'react';

import { CompensationEmptyState } from './components/CompensationEmptyState';
import { CompensationLoadFailedState } from './components/CompensationLoadFailedState';
import { EnvelopeWorkspace } from './components/EnvelopeWorkspace';
import { ReportNavigation } from './components/ReportNavigation';
import { useReportFailure } from './hooks/useReportFailure';

import type { ReportNavigationProps } from './components/ReportNavigation';
import type { CompensationCycleOverviewEnvelope } from '@/types/compensation';

import {
    useCreateCompensationEnvelope,
    useGetCompensationCycleOverview,
    useGetCompensationCycles,
    useGetCompensationEnvelope,
    useSetCompensationCycleBudget,
} from '@/api/compensation';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { Permission } from '@/constants/Permission';
import { useAmelioTranslations } from '@/hooks';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';

interface ActiveEnvelopeOptions {
    envelopes: CompensationCycleOverviewEnvelope[] | undefined;
    chosenEnvelopeId: string | undefined;
}

/** The envelope the reader chose while it is still listed; otherwise (another cycle, a deletion) the
 * first envelope of the cycle. */
const resolveActiveEnvelopeId = ({ envelopes, chosenEnvelopeId }: ActiveEnvelopeOptions) =>
    envelopes?.find((item) => item.id === chosenEnvelopeId)?.id ?? envelopes?.[0]?.id;

/**
 * "Rapport de rémunération": one evaluation cycle, its increase envelopes, and the envelope open.
 */
export const CompensationReportPage = () => {
    const { t } = useAmelioTranslations();
    const reportFailure = useReportFailure();
    const {
        can,
        isLoading: arePermissionsLoading,
        isError: havePermissionsFailed,
        refetch: refetchPermissions,
    } = useUserPermissions();

    const [chosenCycleId, setChosenCycleId] = useState<string | undefined>(undefined);
    const [chosenEnvelopeId, setChosenEnvelopeId] = useState<string | undefined>(undefined);

    const {
        data: cycles,
        isError: haveCyclesFailed,
        refetch: refetchCycles,
    } = useGetCompensationCycles();
    // The server lists the most recent cycle first.
    const selectedCycleId = chosenCycleId ?? cycles?.[0]?.id;
    const {
        data: overview,
        isError: hasOverviewFailed,
        refetch: refetchOverview,
    } = useGetCompensationCycleOverview(selectedCycleId);
    const activeEnvelopeId = resolveActiveEnvelopeId({
        envelopes: overview?.envelopes,
        chosenEnvelopeId,
    });
    const {
        data: envelope,
        isError: hasEnvelopeFailed,
        refetch: refetchEnvelope,
    } = useGetCompensationEnvelope(activeEnvelopeId);

    const { mutateAsync: createEnvelope, isPending: isCreating } = useCreateCompensationEnvelope();
    const { mutateAsync: setCycleBudget } = useSetCompensationCycleBudget();

    const handleCreateEnvelope = async () => {
        if (!selectedCycleId) {
            return;
        }

        // The first "Envelope N" not already taken: after a deletion, a count would repeat a name.
        const takenNames = new Set(overview?.envelopes.map((item) => item.name));
        let number = takenNames.size + 1;
        while (takenNames.has(t('compensation.tabs.newEnvelopeName', { n: number }))) {
            number += 1;
        }

        try {
            const created = await createEnvelope({
                cycleEvaluationId: selectedCycleId,
                name: t('compensation.tabs.newEnvelopeName', { n: number }),
            });
            setChosenEnvelopeId(created.id);
        } catch (error) {
            reportFailure(error, 'compensation.errors.createFailed');
        }
    };

    const handleCycleBudgetChange = async (amount: number | null) => {
        if (!selectedCycleId) {
            return;
        }

        try {
            await setCycleBudget({ cycleId: selectedCycleId, request: { amount } });
        } catch (error) {
            reportFailure(error, 'compensation.errors.cycleBudgetFailed');
        }
    };

    // The permissions are waited for too: read before they arrive, `can` says "no", and someone who
    // manages every department would land on a report with no action to take.
    const areCyclesPending = !cycles && !haveCyclesFailed;
    const isOverviewPending = !!selectedCycleId && !overview && !hasOverviewFailed;

    if (areCyclesPending || arePermissionsLoading || isOverviewPending) {
        return (
            <PageContainer>
                <Skeleton className={'flex flex-col gap-6'}>
                    <Bone className={'h-16 w-full'} />
                    <Bone className={'h-12 w-full'} />
                    <Bone className={'h-96 w-full'} />
                </Skeleton>
            </PageContainer>
        );
    }

    if (havePermissionsFailed || !cycles) {
        return (
            <PageContainer>
                <CompensationLoadFailedState
                    onRetry={() =>
                        void (havePermissionsFailed ? refetchPermissions() : refetchCycles())
                    }
                />
            </PageContainer>
        );
    }

    if (cycles.length === 0) {
        return (
            <PageContainer>
                <EmptyState
                    title={t('compensation.empty.noCycle.title')}
                    message={t('compensation.empty.noCycle.message')}
                />
            </PageContainer>
        );
    }

    const navigation: ReportNavigationProps = {
        cycles,
        selectedCycleId,
        overview,
        activeEnvelopeId,
        managesAllDepartments: can(Permission.ManageAllDepartmentsPerformance),
        isCreating,
        onCycleChange: (cycleId) => {
            setChosenCycleId(cycleId);
            setChosenEnvelopeId(undefined);
        },
        onEnvelopeSelect: setChosenEnvelopeId,
        onCreate: () => void handleCreateEnvelope(),
        onCycleBudgetChange: (amount) => void handleCycleBudgetChange(amount),
    };

    if (envelope) {
        return (
            <EnvelopeWorkspace
                key={`${envelope.id}:${envelope.version}`}
                envelope={envelope}
                navigation={navigation}
                onDeleted={() => setChosenEnvelopeId(undefined)}
                onReload={() => void refetchEnvelope()}
            />
        );
    }

    const renderEnvelopeState = () => {
        if (!overview) {
            return <CompensationLoadFailedState onRetry={() => void refetchOverview()} />;
        }

        if (overview.envelopes.length === 0) {
            return <CompensationEmptyState onCreate={navigation.onCreate} />;
        }

        if (hasEnvelopeFailed) {
            return <CompensationLoadFailedState onRetry={() => void refetchEnvelope()} />;
        }

        return (
            <Skeleton className={'flex flex-col gap-6'}>
                <Bone className={'h-64 w-full'} />
                <Bone className={'h-96 w-full'} />
            </Skeleton>
        );
    };

    return (
        <PageContainer>
            <div className={'flex flex-col gap-6'}>
                <ReportNavigation {...navigation} />
                {renderEnvelopeState()}
            </div>
        </PageContainer>
    );
};
